/**
 * Enemy type definitions.
 *
 * To add a new enemy type:
 *   1. Drop a .glb model into public/models/enemies/
 *   2. Add a new entry here with the config below
 *   3. Add it to SPAWN_WAVES
 *
 * Behavior modes:
 *   'fly_circle'   — orbit above bus at flyHeight, beam attack
 *   'fly_hover'    — hover near bus, strafe side to side
 *   'ground_chase' — (future) run toward bus on terrain
 *   'ground_patrol'— (future) patrol an area on terrain
 */
export const ENEMY_TYPES = {
    // Original UFO — balanced all-rounder (CC0, hat_my_guy)
    ufo: {
        model: '/models/enemies/ufo.glb',
        scale: 3,
        health: 5,
        speed: 15,
        behavior: 'fly_circle',
        flyHeight: 25,
        orbitRadius: 40,
        bobAmplitude: 3,
        bobFrequency: 0.8,
        detectionRange: 150,
        attackRange: 30,
        attackCooldown: 6,
        fuelDamage: 2,
        scoreValue: 150,
        spawnMinDist: 150,
        spawnMaxDist: 350,
        despawnDist: 600,
        beamDuration: 3.0,
        beamColor: 0x66ff66,
        beamRadius: 4,
        beamDamageInterval: 0.5,
    },

    // Classic saucer — fast & agile scout, weaker beam (CC-BY 3.0, Poly by Google)
    ufo_classic: {
        model: '/models/enemies/ufo_classic.glb',
        scale: 4,
        health: 3,
        speed: 22,
        behavior: 'fly_circle',
        flyHeight: 20,
        orbitRadius: 35,
        bobAmplitude: 2,
        bobFrequency: 1.2,
        detectionRange: 180,
        attackRange: 25,
        attackCooldown: 5,
        fuelDamage: 1,
        scoreValue: 100,
        spawnMinDist: 120,
        spawnMaxDist: 300,
        despawnDist: 550,
        beamDuration: 2.0,
        beamColor: 0x44aaff,
        beamRadius: 3,
        beamDamageInterval: 0.4,
    },

    // Mini UFO — tiny, low-flying, fast, hard to hit (CC-BY 3.0, Poly by Google)
    ufo_mini: {
        model: '/models/enemies/ufo_mini.glb',
        scale: 5,
        health: 2,
        speed: 28,
        behavior: 'fly_circle',
        flyHeight: 15,
        orbitRadius: 25,
        bobAmplitude: 1.5,
        bobFrequency: 1.5,
        detectionRange: 120,
        attackRange: 20,
        attackCooldown: 4,
        fuelDamage: 1,
        scoreValue: 80,
        spawnMinDist: 100,
        spawnMaxDist: 250,
        despawnDist: 500,
        beamDuration: 1.5,
        beamColor: 0xff6644,
        beamRadius: 2.5,
        beamDamageInterval: 0.3,
    },

    // Heavy saucer — big, slow, tanky, devastating beam (CC-BY 3.0, Poly by Google)
    ufo_heavy: {
        model: '/models/enemies/ufo_heavy.glb',
        scale: 2.5,
        health: 10,
        speed: 8,
        behavior: 'fly_circle',
        flyHeight: 35,
        orbitRadius: 50,
        bobAmplitude: 4,
        bobFrequency: 0.5,
        detectionRange: 200,
        attackRange: 40,
        attackCooldown: 8,
        fuelDamage: 4,
        scoreValue: 300,
        spawnMinDist: 180,
        spawnMaxDist: 400,
        despawnDist: 650,
        beamDuration: 4.5,
        beamColor: 0xcc44ff,
        beamRadius: 6,
        beamDamageInterval: 0.7,
    },
};

/**
 * Spawn wave definitions — controls when & how enemies appear/disappear.
 *
 * Each wave defines a type of enemy encounter:
 *   type           — key from ENEMY_TYPES
 *   count          — how many to spawn per wave (1–N)
 *   spawnInterval  — [min, max] seconds between wave appearances
 *   lingerDuration — [min, max] seconds UFOs stay before leaving
 *   maxActive      — max concurrent enemies of this type on map
 */
/**
 * Single wave system — one wave at a time, randomly picks a UFO type.
 * Only ever 2 UFOs on screen at once.
 */
export const SPAWN_WAVES = [
    {
        types: ['ufo', 'ufo_classic', 'ufo_mini', 'ufo_heavy'],
        count: 2,
        spawnInterval: [30, 60],
        lingerDuration: [18, 35],
        maxActive: 2,
    },
];
